import { PropsWithChildren } from 'react';
import { AxiosError } from 'axios';

import { instance } from '@/shared/api/cloud';
import { buildLogoutRedirectUrl } from '@/shared/utils/redirect';

import { ConfigContext, ConfigEnv } from './Context';
import { useGetConfig } from './useGetConfig';

export const ConfigProvider = ({ children }: PropsWithChildren) => {
  const { data } = useGetConfig();

  if (!data) {
    return null;
  }

  // Determine the base URL based on the current domain
  const adminHostname = new URL(data.CLIENT_ADMIN_URL).hostname;
  // Check if the current domain ends with the admin base domain
  const normalizedHostname = window.location.hostname
    .replace(/^local\./, '')
    .replace(/^(application|app)\.auto-ops\./, '');
  const isAdminApp = normalizedHostname === adminHostname;
  const appUrl = isAdminApp ? data.CLIENT_ADMIN_URL : data.CLIENT_CLOUD_URL;

  instance.defaults.baseURL = appUrl;

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.status === 401) {
        window.location.href = buildLogoutRedirectUrl(appUrl);
      }
      return Promise.reject(error);
    }
  );

  return (
    <ConfigContext.Provider
      value={{
        launchDarklyId: data.CLIENT_LAUNCH_DARKLY_ID,
        fullStoryOrgId: data.CLIENT_FULL_STORY_ORG_ID,
        apmUrl: data.CLIENT_APM_URL,
        appUrl: appUrl,
        appName: isAdminApp ? 'adminconsole' : 'userconsole',
        autoOpsUrl: data.CLIENT_AUTOOPS_URL,
        autoOpsAdminUrl: data.CLIENT_AUTOOPS_ADMIN_URL,
        env: data.CLIENT_ENV as ConfigEnv,
        isWebhookConnectorEnabled: data.IS_WEBHOOK_CONNECTOR_ENABLED === true,
        isCloudAssistantEnabled: data.IS_CLOUD_ASSISTANT_ENABLED === true,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};
